import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, User, Phone, Mail, Hash, Loader2, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { globalSearch } from '@/services/globalSearchService';
import { getRolePrefix } from '@/hooks/useRolePath';
import { useAuth } from '@/context/AuthContext';

const DEBOUNCE_MS = 300;

const matchIcons = {
  name: User,
  mrn: Hash,
  phone: Phone,
  email: Mail,
};

const GlobalSearch = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [matchType, setMatchType] = useState('name');
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const debounceRef = useRef(null);

  // Keyboard shortcut: Ctrl+K / Cmd+K
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
        setOpen(true);
      }
      if (e.key === 'Escape') {
        setOpen(false);
        inputRef.current?.blur();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Debounced search
  const doSearch = useCallback(async (searchQuery) => {
    if (!searchQuery || searchQuery.trim().length < 2) {
      setResults([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const data = await globalSearch(searchQuery.trim(), 10);
      setResults(data.results || []);
      setMatchType(data.match_type || 'name');
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    setActiveIndex(-1);
    setOpen(true);

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doSearch(val), DEBOUNCE_MS);
  };

  const navigateToPatient = (patient) => {
    const prefix = getRolePrefix(user?.role);
    navigate(`/${prefix}/patient/${patient.id}`);
    setOpen(false);
    setQuery('');
    setResults([]);
  };

  const handleKeyDown = (e) => {
    if (!open || results.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((prev) => (prev < results.length - 1 ? prev + 1 : 0));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((prev) => (prev > 0 ? prev - 1 : results.length - 1));
    } else if (e.key === 'Enter' && activeIndex >= 0) {
      e.preventDefault();
      navigateToPatient(results[activeIndex]);
    }
  };

  const clearSearch = () => {
    setQuery('');
    setResults([]);
    setOpen(false);
    inputRef.current?.focus();
  };

  // Highlight matching text
  const highlightMatch = (text, q) => {
    if (!text || !q) return text;
    const escaped = q.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const parts = text.split(new RegExp(`(${escaped})`, 'gi'));
    return parts.map((part, i) =>
      part.toLowerCase() === q.toLowerCase()
        ? <mark key={i} className="bg-primary/20 text-primary rounded-sm px-0.5">{part}</mark>
        : part
    );
  };

  const MatchIcon = matchIcons[matchType] || User;
  const showDropdown = open && (query.trim().length >= 2);

  return (
    <div ref={containerRef} className="relative mx-2 hidden max-w-md flex-1 md:flex lg:mx-6 xl:max-w-xl">
      <div className="relative w-full">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          ref={inputRef}
          type="text"
          value={query}
          onChange={handleChange}
          onFocus={() => query.trim().length >= 2 && setOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder="Search by name, MRN, phone, or email..."
          className="w-full pl-9 pr-16 h-9 rounded-lg border-slate-300 bg-white text-sm text-foreground shadow-xs placeholder:text-muted-foreground hover:border-slate-400 focus:border-primary focus:bg-white"
          autoComplete="off"
          spellCheck={false}
        />
        {query && (
          <button
            onClick={clearSearch}
            className="absolute right-10 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
        <kbd className="pointer-events-none absolute right-3 top-1/2 hidden h-5 -translate-y-1/2 select-none items-center gap-1 rounded border border-border bg-background px-1.5 font-mono text-2xs text-muted-foreground lg:inline-flex">
          Ctrl K
        </kbd>
      </div>

      {/* Results Dropdown */}
      {showDropdown && (
        <div className="absolute left-0 right-0 top-full z-50 mt-1.5 max-h-[min(440px,calc(100dvh-5rem))] overflow-hidden overflow-y-auto rounded-lg border border-border bg-popover shadow-lg">
          {loading ? (
            <div className="flex items-center justify-center gap-2 p-4 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Searching...
            </div>
          ) : results.length > 0 ? (
            <div>
              <div className="px-3 py-2 border-b border-border">
                <p className="text-xs text-muted-foreground">
                  {results.length} patient{results.length !== 1 ? 's' : ''} found
                  <span className="ml-1 text-muted-foreground/60">
                    - matched by {matchType}
                  </span>
                </p>
              </div>
              {results.map((patient, index) => (
                <button
                  key={patient.id}
                  onClick={() => navigateToPatient(patient)}
                  onMouseEnter={() => setActiveIndex(index)}
                  className={`
                    w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors
                    ${activeIndex === index ? 'bg-clinical-soft' : 'hover:bg-muted/60'}
                  `}
                >
                  {/* Avatar */}
                  <div className="flex-shrink-0 w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center">
                    <MatchIcon className="h-4 w-4 text-primary" />
                  </div>

                  {/* Patient Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-foreground truncate">
                        {highlightMatch(patient.name, query)}
                      </span>
                      {patient.gender && (
                        <span className="text-2xs text-muted-foreground">
                          {patient.gender}{patient.age ? `, ${patient.age}y` : ''}
                        </span>
                      )}
                    </div>
                    <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-1">
                      {patient.mrn && (
                        <span className="text-xs text-muted-foreground flex items-center gap-1">
                          <Hash className="h-3 w-3" />
                          {highlightMatch(patient.mrn, query)}
                        </span>
                      )}
                      {patient.phone && (
                        <span className="text-xs text-muted-foreground flex items-center gap-1">
                          <Phone className="h-3 w-3" />
                          {highlightMatch(patient.phone, query)}
                        </span>
                      )}
                      {patient.email && (
                        <span className="text-xs text-muted-foreground flex items-center gap-1 truncate">
                          <Mail className="h-3 w-3" />
                          {highlightMatch(patient.email, query)}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Status badge */}
                  <Badge variant={patient.status === 'active' ? 'default' : 'secondary'} className="text-2xs flex-shrink-0">
                    {patient.status}
                  </Badge>
                </button>
              ))}
              {/* Keyboard hint */}
              <div className="px-3 py-1.5 border-t border-border bg-muted/30">
                <div className="flex items-center gap-3 text-2xs text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <kbd className="rounded border border-border bg-background px-1 text-2xs">Up</kbd>
                    <kbd className="rounded border border-border bg-background px-1 text-2xs">Down</kbd>
                    navigate
                  </span>
                  <span className="flex items-center gap-1">
                    <kbd className="rounded border border-border bg-background px-1 text-2xs">Enter</kbd>
                    select
                  </span>
                  <span className="flex items-center gap-1">
                    <kbd className="px-1.5 rounded border border-border bg-background text-2xs">esc</kbd>
                    close
                  </span>
                </div>
              </div>
            </div>
          ) : (
            <div className="p-4 text-center">
              <Search className="h-8 w-8 text-muted-foreground/30 mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">No patients found</p>
              <p className="text-xs text-muted-foreground/60 mt-0.5">
                Try searching by name, MRN, phone number, or email
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default GlobalSearch;
